/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTitleLayout, ControlBtnGroup, SubTitleGroup } from 'components/layouts/ContentLayout';
import MapChart from 'components/mapchart/MapChart';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import { useLoading } from 'components/process/Loading';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { useMessageBar } from 'hooks/useMessageBar';
import PropTypes from 'prop-types';
import * as input from '@grapecity/wijmo.input';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import {
  findSetupMapChart,
  findSetupMapChartAuth,
  findSetupMapChartList,
  saveSetupMapChart,
  saveSetupMapChartImg,
} from 'apis/setup/SetupMapChart';
import { SetupMapChart, SetupMapChartCondition } from 'models/setup/SetupMapChart';
import { Code } from 'models/common/CommonCode';
import { SetupProject } from 'models/setup/SetupProject';
import { findProject } from 'apis/setup/SetupProject';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import { BizName, FileTypeName } from 'models/common/Common';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { tabs } from 'components/layouts/Tab';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import CustomGrid from 'components/grids/CustomGrid';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { getExcelFileName } from 'utils/ExcelUtil';
import { NormalButton } from 'components/buttons/ButtonStyle';
import useSessionStore from 'stores/useSessionStore';
import { hasRole } from 'utils/SessionUtil';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';

const SetupProjectPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { elmPrjId } = useSessionStore();
  const { openCommonModal } = useCommonModal();

  const defaultCondition = {
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjId: elmPrjId || '',
  };
  const ATCH_FILE_TP_CD = 'SETUP_MAP_CHART_IMG';

  const gridRef = useRef<any>();
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [setupMapChartCondition, setSetupMapChartCondition] =
    useState<SetupMapChartCondition>(defaultCondition);
  const [mapChartInfo, setMapChartInfo] = useState<SetupMapChart>({});
  const [errors, setErrors] = useState<any>({});
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [rowData, setRowData] = useState<SetupMapChart[]>([]);
  const [noneCoordinateData, setNoneCoordinateData] = useState<SetupMapChart[]>([]);
  const [backupRowData, setBackupRowData] = useState<SetupMapChart[]>([]);
  const [isOpenFileUploadPopup, setIsOpenFileUploadPopup] = useState(false);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [isPrjAuth, setIsPrjAuth] = useState<boolean>(false);
  const [isDownloadImg, setIsDownloadImg] = useState(false);
  const [downloadImgParam, setDownloadImgParam] = useState<any>({});

  const tabMenus = useMemo(() => {
    return [
      { id: 'LIST', name: t('setup.label.설비 Mapping', '설비 Mapping') },
      { id: 'MAP', name: t('setup.label.Map Chart', 'Map Chart') },
    ];
  }, []);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (setupMapChartCondition?.bildPlntCds || []).length > 0
          ? setupMapChartCondition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, setupMapChartCondition?.bildPlntCds]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'eqpId',
        visible: true,
        header: String(t('setup.label.설비ID', '설비ID')),
        width: 240,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'eqpNm',
        visible: true,
        header: String(t('setup.label.설비명', '설비명')),
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'minXcrd',
        visible: true,
        header: String(t('setup.label.XMin', 'XMin')),
        align: 'center',
        format: 'n4',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n4',
        }),
      },
      {
        binding: 'maxXcrd',
        visible: true,
        header: String(t('setup.label.XMax', 'XMax')),
        align: 'center',
        format: 'n4',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n4',
        }),
      },
      {
        binding: 'minYcrd',
        visible: true,
        header: String(t('setup.label.YMin', 'YMin')),
        align: 'center',
        format: 'n4',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n4',
        }),
      },
      {
        binding: 'maxYcrd',
        visible: true,
        header: String(t('setup.label.YMax', 'YMax')),
        align: 'center',
        format: 'n4',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n4',
        }),
      },
      {
        binding: 'ctrXcrd',
        visible: true,
        header: String(t('setup.label.XCenter', 'XCenter')),
        align: 'center',
        format: 'n4',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n4',
        }),
      },
      {
        binding: 'ctrYcrd',
        visible: true,
        header: String(t('setup.label.YCenter', 'YCenter')),
        align: 'center',
        format: 'n4',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n4',
        }),
      },
    ];
  }, [rowData]);

  const drwImageSize = useMemo(() => {
    if (mapChartInfo?.imgWthLen && mapChartInfo?.imgVtcLen) {
      return `${mapChartInfo.imgWthLen} * ${mapChartInfo.imgVtcLen}`;
    } else {
      return ``;
    }
  }, [mapChartInfo]);

  const downloadExcelTemplate = async (templateType) => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    openLoading(true);
    downloadExcelTemplatesPost(templateType, mapChartInfo?.elmPrjId || '', []).finally(() =>
      openLoading(false)
    );
  };

  const callbackExcelUpload = async (file: FileList | null) => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      FileTypeName.TPL_SETUP_MAP_CHART,
      'NORMAL',
      'TB_EELMB_PRJ_TGT_EQP_M',
      setupMapChartCondition?.elmPrjId
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          btnSearch(setupMapChartCondition);
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const btnSearch = (condition: SetupMapChartCondition) => {
    // validation check
    if (!validate()) return;

    openLoading(true);
    Promise.all([
      findSetupMapChart(condition?.elmPrjId || ''),
      findSetupMapChartList(condition),
      findSetupMapChartAuth(condition?.elmPrjId || ''), // 프로젝트 담당자(본사, 법인) 권한 체크
    ])
      .then(([res1, res2, auth]) => {
        if (res1 !== null) {
          setMapChartInfo({ ...res1 });
        }

        if (res2 !== null) {
          setRowData(res2.map((o) => ({ ...o, selected: true })) || []);
          setBackupRowData(res2.map((o) => ({ ...o, selected: true })) || []);
          // 좌표등록되지 않은 설비 카운트
          const noneCoordiData = res2.filter(
            (o) =>
              o.maxXcrd === null ||
              o.minXcrd === null ||
              o.maxYcrd === null ||
              o.minYcrd === null ||
              o.ctrXcrd === null ||
              o.ctrYcrd === null
          );
          setNoneCoordinateData(noneCoordiData);
        }
        setIsPrjAuth(!!auth);
      })
      .catch(() => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const handleOnChange = (name: string, value: any) => {
    setSetupMapChartCondition((prev) => ({ ...prev, [name]: value }));
    if (name !== 'bildPlntCds') setErrors((prev: any) => ({ ...prev, [name]: !value }));
  };

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCode);

    findProject({ searchVisibleSaveYn: 'N' }).then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const validate = () => {
    const err = {};

    // 프로젝트
    if (!setupMapChartCondition?.elmPrjId) {
      Object.assign(err, { elmPrjId: true });
    }

    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  const openImgFileUpload = () => {
    // validation check
    if (!validate()) return;

    setIsDownloadImg(false);
    setDownloadImgParam({
      atchFileTpCd: ATCH_FILE_TP_CD,
      optValCtn1: 'tb_eelmb_prj_m',
      bizName: BizName.SETUP,
    });
    setIsOpenFileUploadPopup(true);
  };

  /**
   * 도면 이미지 파일 삭제
   */
  const onDeleteImg = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.도면 이미지를 삭제하시겠습니까?`, `도면 이미지를 삭제하시겠습니까?`),
      yesCallback: async () => {
        callbackFileUpload('');
      },
    });
  };

  /**
   * 도면 이미지 다운로드
   */
  const onDownloadImg = () => {
    setIsDownloadImg(true);
    setDownloadImgParam({
      atchFileGrId: mapChartInfo.drwAtchFileId,
      atchFileTpCd: ATCH_FILE_TP_CD,
      optValCtn1: 'tb_eelmb_prj_m',
      bizName: BizName.SETUP,
    });
    setIsOpenFileUploadPopup(true);
  };

  /**
   * 도면 이미지 파일 저장
   */
  const callbackFileUpload = async (atchFileGrId: string) => {
    openLoading(true);
    const res = await saveSetupMapChartImg({
      ...setupMapChartCondition,
      drwAtchFileId: atchFileGrId,
    });
    if (res.data) {
      openMessageBar({
        type: 'confirm',
        content: t('com.msg.저장되었습니다.', '저장되었습니다.'),
      });
      setIsOpenFileUploadPopup(false);
      btnSearch(setupMapChartCondition);
    }
    openLoading(false);
  };

  /***** Grid 관련 함수 start *****/
  // Grid 새로고침
  const onResetGrid = () => {
    setRowData(backupRowData.map((o) => ({ ...o })));
  };
  // Grid 초기세팅
  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.cells) {
        const col = s.columns[e.col];
        const item = s.rows[e.row].dataItem;

        const value = item[col.binding];
        // 데이터가 비어있으면 배경색 처리
        if (value === null || value === undefined || value === '') {
          e.cell.classList.add('!bg-red-100');
        } else {
          e.cell.classList.remove('!bg-red-100');
        }
      }
    });
  };
  // Grid Edit Event
  const onBeginningEdit = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 설비id, 설비명은 수정 불가능 처라
    if (['eqpId', 'eqpNm'].includes(binding)) {
      e.cancel = true;
    }
  };
  // Grid Cell Edit 완료시 Event
  const onCellEditEnded = (grid, e) => {
    grid.collectionView.refresh();
  };
  /***** Grid 관련 함수 end ******/

  const handleDownloadExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('setup.label.MapChart', 'MapChart');
    book.saveAsync(getExcelFileName(t('setup.label.MapChart', 'MapChart')));
  };

  const saveRowData = () => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    const saveData = rowData.filter((o) => o.crudKey === 'U');

    if (saveData.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.수정한 항목이 없습니다.', '수정한 항목이 없습니다.'),
      });
      return;
    }

    // 좌표가 모두 비워저 있지 않고, 하나 이상 비어있는 경우
    const fields = ['minXcrd', 'maxXcrd', 'minYcrd', 'maxYcrd', 'ctrXcrd', 'ctrYcrd'];
    const chkFields = saveData.map((o) => {
      const values = fields.map((f) => o[f]);
      return values.some((v) => !_.isNil(v)) && values.some((v) => _.isNil(v));
    });
    if (chkFields.some((b) => b)) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.입력하지 않은 항목이 있습니다.', '입력하지 않은 항목이 있습니다.'),
      });
      return;
    }

    saveSetupMapChart(saveData)
      .then((res) => {
        if (res.data) {
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          btnSearch({ elmPrjId: mapChartInfo.elmPrjId || '' });
        }
      })
      .finally(() => openLoading(false));
  };

  const removeRowData = () => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    const rmRowData: SetupMapChart[] = gridRef.current.rows
      .filter((r) => r.isSelected)
      .map((o) => ({
        ...o.dataItem,
        ctrXcrd: null,
        minXcrd: null,
        maxXcrd: null,
        ctrYcrd: null,
        minYcrd: null,
        maxYcrd: null,
      }));
    if (rmRowData.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.삭제할 데이터를 선택해주세요.', '삭제할 데이터를 선택해주세요.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t(
        `com.msg.선택된 항목의 좌표를 삭제하시겠습니까?`,
        `선택된 항목의 좌표를 삭제하시겠습니까?`
      ),
      yesCallback: async () => {
        openLoading(true);
        saveSetupMapChart(rmRowData)
          .then((res) => {
            if (res.data) {
              openMessageBar({
                type: 'confirm',
                content: t('com.label.삭제되었습니다.', '삭제되었습니다.'),
              });
              btnSearch({ elmPrjId: mapChartInfo.elmPrjId || '' });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.삭제 중 에러가 발생했습니다.', '삭제 중 에러가 발생했습니다.'),
            });
          })
          .finally(() => {
            openLoading(false);
          });
      },
    });
  };

  useEffect(() => {
    initCodes();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupMapChartCondition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          setupMapChartCondition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className="dot">{String(t('setup.label.프로젝트명', '프로젝트명'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={setupMapChartCondition?.elmPrjId}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.msg.프로젝트를 선택해 주세요.', '프로젝트를 선택해 주세요.')
                  )}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setSetupMapChartCondition}
              onSearch={() => btnSearch(setupMapChartCondition)}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.맵차트 등록', '맵차트 등록')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('setup.label.공장 Layout', '공장 Layout')}</TableCell>
              <TableCell>
                <span className="ellipsis">
                  <NormalButton
                    css={IconButton.button}
                    size="sm"
                    className="icoNone mr"
                    onClick={openImgFileUpload}
                  >
                    {t('com.button.이미지 등록', '이미지 등록')}
                  </NormalButton>
                  {mapChartInfo.drwAtchFileNm}
                  {mapChartInfo.drwAtchFileId && (
                    <>
                      <NormalButton
                        css={IconButton.button}
                        size="sm"
                        className="icoNone mr"
                        onClick={onDownloadImg}
                      >
                        {t('com.button.다운로드', '화면 다운로드')}
                      </NormalButton>
                      <NormalButton
                        css={IconButton.button}
                        size="sm"
                        className="icoNone mr"
                        onClick={onDeleteImg}
                      >
                        {t('com.button.삭제', '삭제')}
                      </NormalButton>
                    </>
                  )}
                </span>
              </TableCell>
              <TableCell>{t('setup.label.Size', 'Size')}</TableCell>
              <TableCell>{drwImageSize}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <div css={tabs.wrap} className="mt-6">
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`setup-schedule-tab-${idx}`}
              aria-controls={`setup-schedule-tabpanel-${idx}`}
            />
          ))}
          {currentTabIndex === 0 && (
            <SubTitleGroup>
              <div className="info warning">
                {t('com.label.총', '총')}
                <span> {(noneCoordinateData || []).length.toLocaleString()}</span>
                {t(
                  'com.label.건의 설비에 좌표 정보가 등록되어 있지 않습니다.',
                  '건의 설비에 좌표 정보가 등록되어 있지 않습니다.'
                )}
              </div>
            </SubTitleGroup>
          )}
        </Tabs>
        {currentTabIndex === 0 && (
          <div css={tabs.btn}>
            <Button
              css={IconButton.button}
              className="find"
              onClick={() => setRowData(noneCoordinateData)}
              disableRipple
            >
              {t('com.button.좌표미등록설비', '좌표미등록설비')}
            </Button>
            {(isPrjAuth || hasRole('FA_MANAGER')) && (
              <>
                <Button
                  css={IconButton.button}
                  className="Exceldown"
                  onClick={() => downloadExcelTemplate(FileTypeName.TPL_SETUP_MAP_CHART)}
                  disableRipple
                >
                  {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
                </Button>
                <ExcelUploadButton onCallback={(file) => callbackExcelUpload(file)} />
                <Button css={IconButton.button} className="save" onClick={saveRowData}>
                  {t('com.button.저장', '저장')}
                </Button>
                <Button css={IconButton.button} className="delete" onClick={removeRowData}>
                  {t('com.button.삭제', '삭제')}
                </Button>
              </>
            )}
            <Button
              css={IconButton.button}
              className="Exceldown"
              onClick={() => handleDownloadExcel()}
              disableRipple
            >
              {t('com.button.다운로드', '화면 다운로드')}
            </Button>
            <Button css={IconButton.button} className="refresh" onClick={onResetGrid}>
              {t('com.button.새로고침', '새로고침')}
            </Button>
          </div>
        )}
      </div>
      <TabPanel value={currentTabIndex} index={0}>
        <CustomGrid
          id={'mapchart-grid-id'}
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={450}
          align={'center'}
          isSelector={true}
          initialized={onInitialized}
          beginningEdit={onBeginningEdit}
          cellEditEnded={onCellEditEnded}
          onChangeCheckedItem={(item) => console.log(item)}
        />
      </TabPanel>
      <TabPanel value={currentTabIndex} index={1}>
        {rowData.length > 0 && mapChartInfo.drwAtchFileId && (
          <MapChart
            className="border height_1"
            polygonData={rowData}
            setPolygonData={setRowData}
            imageId={mapChartInfo.drwAtchFileId || ''}
            imageWidth={parseInt(mapChartInfo.imgWthLen || '0')}
            imageHeight={parseInt(mapChartInfo.imgVtcLen || '0')}
            showLegend={false}
            showTooltip={true}
            badgeKey="no-key"
          />
        )}
      </TabPanel>

      {isOpenFileUploadPopup && (
        // 도면 이미지 업로드 Modal
        <FileUploadPopUp
          open={isOpenFileUploadPopup}
          close={() => {
            setIsOpenFileUploadPopup(false);
          }}
          singleSelect={true}
          showAllDownload={false}
          downloadOnly={isDownloadImg}
          initParam={downloadImgParam}
          onCallback={(atchFileGrId, fileCount) => {
            if (atchFileGrId && fileCount > 0) {
              callbackFileUpload(atchFileGrId);
            }
          }}
        />
      )}
      {isExcelValidModalOpen && (
        // 엑셀 템플릿 업로드 검증 Modal
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-tabpanel-${index}`}>
      <div>{children}</div>
    </div>
  );
}

export default SetupProjectPage;
